// AI-CONTEXT: See docs/features/portfolio-maatschappelijke-waarde.md
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import formatEuro from '../../helpers/formatEuro'
import usePortfolioSocietalValue from '../../hooks/portfolio/usePortfolioSocietalValue'
import StaticCardLabel from './StaticCardLabel'
import KpiValue from './KpiValue'

/** De naam van elke stroom, zoals de lezer hem kent van de detailpagina. */
const STREAM_LABELS = {
    participation: 'de participatieladder',
    msi: 'de effecten uit de meetstandaard',
    materials: 'materialen en adviezen',
}

const ALLE_STROMEN = ['participation', 'msi', 'materials']

/**
 * Wat er NIET in dit bedrag zit, in gewone taal.
 *
 * Valt in de nachtrun een bron weg (de interventiebibliotheek bijvoorbeeld),
 * dan telt die stroom niet mee. Het bedrag is dan niet fout maar wel
 * onvolledig, en zonder deze regel presenteert de kaart het als het geheel.
 *
 * @param {Array<string>|undefined} streams - De stromen die er wél in zitten
 * @returns {string} Lege string als het bedrag compleet is
 */
const missingStreamNote = (streams) => {
    if (!Array.isArray(streams) || streams.length === 0) return ''

    const missend = ALLE_STROMEN.filter((stroom) => !streams.includes(stroom))
    if (missend.length === 0) return ''

    return `Zonder ${missend.map((stroom) => STREAM_LABELS[stroom]).join(' en ')}.`
}

/**
 * KPI-tegel voor een portfolio: de Maatschappelijke waarde van alle projecten
 * eronder bij elkaar, met doorklik naar de uitsplitsing per project.
 *
 * De cijfers komen uit de nachtrun (`TenantMonetaryValue`) en niet uit een lees
 * hier: een portfolio mag de collecties van zijn projecten niet lezen, en een
 * geweigerde read geeft geen fout maar een lege lijst.
 *
 * Drie dingen worden apart gehouden, omdat ze alle drie als 'een bedrag' zouden
 * kunnen verschijnen terwijl ze iets anders betekenen:
 *   - nog aan het laden          → geen bewering
 *   - geen projecten             → dat staat er met zoveel woorden
 *   - projecten zonder berekening → het bedrag is te laag, en dat staat erbij
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 */
const DashboardPortfolioValueCard = () => {
    const history = useHistory()
    const { data, loading, error } = usePortfolioSocietalValue()

    const naarDetail = () => history.push(`/${client}/portfolio-maatschappelijke-waarde`)

    const aantalZonder = Array.isArray(data?.pending) ? data.pending.length : 0
    const geenProjecten = data !== null && (data.projects || []).length === 0 && aantalZonder === 0
    const streamNote = missingStreamNote(data?.streams)

    return (
        <div
            className='participation-value-card participation-value-card--clickable'
            role='button'
            tabIndex={0}
            onClick={naarDetail}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    naarDetail()
                }
            }}
        >
            <StaticCardLabel
                title='Maatschappelijke waarde projecten'
                pill={{ variant: 'portfolio', label: 'Portfolio' }}
            />

            {error ? (
                <p className='participation-value-error'>
                    We konden de waarde van je projecten niet ophalen.
                </p>
            ) : (
                <>
                    <KpiValue
                        loading={loading}
                        value={data === null || geenProjecten ? null : formatEuro(data.total)}
                    />

                    {geenProjecten &&
                        <p className='participation-value-note'>Er hangen nog geen projecten onder dit portfolio.</p>
                    }

                    {aantalZonder > 0 &&
                        <p className='participation-value-note'>
                            Van {aantalZonder} {aantalZonder === 1 ? 'project' : 'projecten'} is nog geen bedrag
                            berekend; die tellen hier niet mee.
                        </p>
                    }

                    {streamNote &&
                        <p className='participation-value-note'>{streamNote}</p>
                    }
                </>
            )}

            <span className='participation-value-more'>Meer info →</span>
        </div>
    )
}

export default DashboardPortfolioValueCard
